// The chat SSE client (beekeeper-agent-chat, CONTRACT.md §6/§7; `client-beekeeper-chat`): a thin
// transport over the four rounds chat routes — POST a message (opens a turn's live event stream),
// GET a turn's events after a cursor (reattach), POST stop, GET the thread. Modelled on
// `net/rounds-http.ts`'s own shape (a scoped client over `baseUrl`/`scope`/`token`, `fetchImpl`
// injectable, malformed wire data degrading to `null`/skip rather than throwing) — plus ONE further
// seam the two streaming routes need: `openStream`.
//
// `EventSource` cannot serve here even where it exists (a real browser): it is GET-only with no
// custom headers and no body, so it cannot carry the POST route's message body or either route's
// `Authorization: Bearer` header. `fetch`'s streaming response body can, but `ReadableStream`
// plumbing is exactly the kind of thing a test should not have to stand up — and the node test
// project has no `EventSource` at all. So `openStream` is injected exactly as `rounds-http.ts`
// injects `fetchImpl`: production wires `createFetchTurnStream`, a test hands the client a
// synchronous fake that calls the handlers with whatever `TurnEventDto` sequence the scenario wants.

/** One SSE frame's decoded payload — CONTRACT.md §6's `TurnEventDto`. */
export interface TurnEventDto {
  turnId: string;
  /** Monotonic within the turn, starts at 1. */
  cursor: number;
  /** The terminal tick of the player's last completed run, or 0. Never a wall clock. */
  tick: number;
  kind: 'turnStarted' | 'textDelta' | 'assistantMessage' | 'toolStarted' | 'toolFinished' | 'turnFinished';
  /** `kind === 'textDelta' | 'assistantMessage'`. */
  text?: string;
  /** `kind === 'toolStarted' | 'toolFinished'`. */
  callId?: string;
  tool?: string;
  /** `kind === 'toolFinished'`. */
  status?: 'ok' | 'error' | 'skipped';
  summary?: string;
  detail?: string;
  /** `kind === 'turnFinished'`. */
  reason?: string;
  version?: number | null;
}

export interface ThreadToolCallDto {
  callId: string;
  tool: string;
  status: 'ok' | 'error' | 'skipped';
  summary: string;
  detail: string;
}

export interface ThreadMessageDto {
  id: string;
  role: 'player' | 'agent';
  text: string;
  toolCalls?: ThreadToolCallDto[];
}

export interface ThreadTurnDto {
  turnId: string;
  reason: string;
  version: number | null;
}

export type ThreadItemDto =
  | { kind: 'message'; id: string; role: 'player' | 'agent'; text: string }
  | ({ kind: 'tool' } & ThreadToolCallDto)
  | ({ kind: 'terminal' } & ThreadTurnDto);

export interface ThreadInFlightDto {
  turnId: string;
  /** How far the **turn** has got — a progress report, NOT a resume point. The server renders a
   * turn's agent text and tool evidence into `messages` only when the turn settles, so a thread
   * fetched mid-turn is complete only through cursor 0 of this turn; rehydration therefore
   * re-attaches with `afterCursor: 0` (see `transcript-model.ts`'s `transcriptFromThread`). */
  cursor: number;
}

/** `GET /rounds/{sandbox}/chat/thread`'s body — CONTRACT.md §6's `ThreadDto`. */
export interface ThreadDto {
  /** Ordered player-visible evidence. New servers always supply this one stream. */
  items?: ThreadItemDto[];
  /** Legacy read shape accepted only while old persisted/test fixtures are migrated. */
  messages: ThreadMessageDto[];
  turns: ThreadTurnDto[];
  /** Load-bearing for reload-mid-turn (client-beekeeper-chat "re-attaches to an in-flight turn"). */
  inFlight: ThreadInFlightDto | null;
}

const MAX_THREAD_ITEMS = 1_000;
const MAX_THREAD_MESSAGE_CHARS = 2_000;

function threadItemDtoFromJson(json: unknown): ThreadItemDto | null {
  if (json === null || typeof json !== 'object' || Array.isArray(json)) return null;
  const row = json as Record<string, unknown>;
  if (row['kind'] === 'message') {
    const message = messageDtoFromJson(row);
    return message === null ? null : { kind: 'message', id: message.id, role: message.role, text: message.text };
  }
  if (row['kind'] === 'tool') {
    const tool = toolCallDtoFromJson(row);
    return tool === null ? null : { kind: 'tool', ...tool };
  }
  if (row['kind'] === 'terminal') {
    const turn = turnDtoFromJson(row);
    return turn === null ? null : { kind: 'terminal', ...turn };
  }
  return null;
}

/** A stream failure, discriminated by whether the server ever definitively answered
 * (`beekeeper-progress-indicator` D-C). One `OpenTurnStream` serves both the send (POST) and
 * reattach (GET) legs, so this type carries only what the transport itself can know; which phase a
 * caller may fold a given kind into (e.g. the send leg's `failed{refused}`, never the reattach
 * leg's) is a decision made by the caller, not here.
 *
 * - `refusal`: the server answered with a non-2xx response — admission is known, and known to have
 *   failed.
 * - `transport`: the connection never completed, or failed while reading the body — admission is
 *   simply unknown; it may have succeeded on the server without the client ever finding out. */
export type TurnStreamError =
  | {
      kind: 'refusal';
      status: number;
      /** A sanitized, length-bounded extract of the server's `error` body field
       * (`beekeeper-progress-indicator` task 4.5) — `undefined` for a non-JSON body, a body with no
       * `error` string, or an empty one, all of which a reason whitelist must read the same way as
       * any other unrecognised reason: unknown, never assumed safe to resend. */
      reason?: string;
    }
  | { kind: 'transport'; cause: unknown };

export interface TurnStreamHandlers {
  onEvent(event: TurnEventDto): void;
  /** The server closed the stream in an orderly way (the response body ended). A live turn's own
   * end is signalled by a `turnFinished` event, not by this — `onDone` fires after that too, since
   * the server closes the response once the terminal event has been sent. */
  onDone(): void;
  /** A transport failure (non-2xx response, a dropped connection, a body that could not be read). */
  onError(err: TurnStreamError): void;
}

export interface SseSubscription {
  close(): void;
}

export interface OpenStreamRequest {
  method: 'POST' | 'GET';
  url: string;
  headers: Record<string, string>;
  body?: string;
}

/** The injectable stream factory (CONTRACT.md §7): opens one HTTP request expected to answer
 * `text/event-stream` and reports each decoded `TurnEventDto` as it arrives. */
export type OpenTurnStream = (request: OpenStreamRequest, handlers: TurnStreamHandlers) => SseSubscription;

const KNOWN_KINDS: ReadonlySet<string> = new Set([
  'turnStarted',
  'textDelta',
  // Additive (E2E round-1 finding M-1, 2026-08-09): a complete, standalone assistant utterance for
  // one model call, as opposed to `textDelta`'s incremental fragment — a canned/scripted backend
  // can legitimately re-say its whole line once per internal model call within a single turn
  // (`docs/dev/architecture/beekeeper-agent-chat.md`'s `Calling → Dispatching → Calling` loop), and
  // `textDelta`'s accretion (`m.text + event.text`) is exactly wrong for that: two such frames
  // smear into one bubble reading the same sentence twice. `transcript-model.ts` always opens a
  // fresh message for one of these rather than appending to whatever is already open.
  'assistantMessage',
  'toolStarted',
  'toolFinished',
  'turnFinished',
]);
const KNOWN_STATUSES: ReadonlySet<string> = new Set(['ok', 'error', 'skipped']);

/** Decodes one SSE frame's `data:` JSON into a {@link TurnEventDto}. `null` for anything that is
 * not a well-formed one (malformed JSON, a missing `turnId`/`cursor`/`kind`, or an unrecognised
 * `kind`) — a garbled frame is dropped rather than fabricating a half-built event. */
export function turnEventFromJson(json: unknown): TurnEventDto | null {
  if (json === null || typeof json !== 'object') {
    return null;
  }
  const row = json as Record<string, unknown>;
  if (
    typeof row['turnId'] !== 'string' ||
    typeof row['cursor'] !== 'number' ||
    typeof row['kind'] !== 'string' ||
    !KNOWN_KINDS.has(row['kind'])
  ) {
    return null;
  }
  const version = row['version'];
  return {
    turnId: row['turnId'],
    cursor: row['cursor'],
    tick: typeof row['tick'] === 'number' ? row['tick'] : 0,
    kind: row['kind'] as TurnEventDto['kind'],
    text: typeof row['text'] === 'string' ? row['text'] : undefined,
    callId: typeof row['callId'] === 'string' ? row['callId'] : undefined,
    tool: typeof row['tool'] === 'string' ? row['tool'] : undefined,
    status: typeof row['status'] === 'string' && KNOWN_STATUSES.has(row['status'])
      ? (row['status'] as TurnEventDto['status'])
      : undefined,
    summary: typeof row['summary'] === 'string' ? row['summary'] : undefined,
    detail: typeof row['detail'] === 'string' ? row['detail'] : undefined,
    reason: typeof row['reason'] === 'string' ? row['reason'] : undefined,
    version: typeof version === 'number' ? version : version === null ? null : undefined,
  };
}

function toolCallDtoFromJson(json: unknown): ThreadToolCallDto | null {
  if (json === null || typeof json !== 'object') {
    return null;
  }
  const row = json as Record<string, unknown>;
  if (
    typeof row['callId'] !== 'string' ||
    typeof row['tool'] !== 'string' ||
    typeof row['status'] !== 'string' ||
    !KNOWN_STATUSES.has(row['status'])
  ) {
    return null;
  }
  return {
    callId: row['callId'],
    tool: row['tool'],
    status: row['status'] as ThreadToolCallDto['status'],
    summary: typeof row['summary'] === 'string' ? row['summary'] : '',
    detail: typeof row['detail'] === 'string' ? row['detail'] : '',
  };
}

function messageDtoFromJson(json: unknown): ThreadMessageDto | null {
  if (json === null || typeof json !== 'object') {
    return null;
  }
  const row = json as Record<string, unknown>;
  if (
    typeof row['id'] !== 'string' ||
    (row['role'] !== 'player' && row['role'] !== 'agent') ||
    typeof row['text'] !== 'string' ||
    Array.from(row['text']).length > MAX_THREAD_MESSAGE_CHARS
  ) {
    return null;
  }
  const rawCalls = Array.isArray(row['toolCalls']) ? row['toolCalls'] : [];
  const toolCalls: ThreadToolCallDto[] = [];
  for (const entry of rawCalls) {
    const call = toolCallDtoFromJson(entry);
    if (call !== null) {
      toolCalls.push(call);
    }
  }
  return { id: row['id'], role: row['role'], text: row['text'], toolCalls };
}

function turnDtoFromJson(json: unknown): ThreadTurnDto | null {
  if (json === null || typeof json !== 'object') {
    return null;
  }
  const row = json as Record<string, unknown>;
  if (typeof row['turnId'] !== 'string' || typeof row['reason'] !== 'string') {
    return null;
  }
  return {
    turnId: row['turnId'],
    reason: row['reason'],
    version: typeof row['version'] === 'number' ? row['version'] : null,
  };
}

function inFlightFromJson(json: unknown): ThreadInFlightDto | null {
  if (json === null || typeof json !== 'object') {
    return null;
  }
  const row = json as Record<string, unknown>;
  if (typeof row['turnId'] !== 'string' || typeof row['cursor'] !== 'number') {
    return null;
  }
  return { turnId: row['turnId'], cursor: row['cursor'] };
}

/** Decodes `GET /chat/thread`'s body. `null` for anything that is not a thread at all (a non-object
 * body) — individual malformed messages/turns are dropped rather than failing the whole read. */
export function threadDtoFromJson(json: unknown): ThreadDto | null {
  if (json === null || typeof json !== 'object' || Array.isArray(json)) {
    return null;
  }
  const row = json as Record<string, unknown>;
  let orderedItems: ThreadItemDto[] | undefined;
  if ('items' in row) {
    if (!Array.isArray(row['items']) || row['items'].length > MAX_THREAD_ITEMS) return null;
    orderedItems = [];
    for (const entry of row['items']) {
      const item = threadItemDtoFromJson(entry);
      // Unknown/malformed ordered evidence is a contract failure: silently dropping it would
      // manufacture a chronology that never happened.
      if (item === null) return null;
      orderedItems.push(item);
    }
  }
  const rawMessages = Array.isArray(row['messages']) ? row['messages'] : [];
  const messages: ThreadMessageDto[] = [];
  for (const entry of rawMessages) {
    const message = messageDtoFromJson(entry);
    if (message !== null) {
      messages.push(message);
    }
  }
  const rawTurns = Array.isArray(row['turns']) ? row['turns'] : [];
  const turns: ThreadTurnDto[] = [];
  for (const entry of rawTurns) {
    const turn = turnDtoFromJson(entry);
    if (turn !== null) {
      turns.push(turn);
    }
  }
  return { items: orderedItems, messages, turns, inFlight: inFlightFromJson(row['inFlight']) };
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

async function safeResponseJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/** The longest refusal reason a client will hold onto — the same order of magnitude as
 * `rounds_chat`'s own reason strings, generous enough for any of them and bounded against a
 * hostile or buggy intermediary's body. */
const MAX_REFUSAL_REASON_CHARS = 200;

/** Extracts a {@link TurnStreamError}'s `reason` from a refused response's body: the `error`
 * field, sanitized and length-bounded. `undefined` — never a fabricated string — for a non-JSON
 * body, a body with no `error` string, or one that is empty after cleaning; a caller's reason
 * whitelist reads all three exactly like any other unrecognised reason. */
async function extractRefusalReason(response: Response): Promise<string | undefined> {
  const body = await safeResponseJson(response);
  if (body === null || typeof body !== 'object' || Array.isArray(body)) {
    return undefined;
  }
  const raw = (body as Record<string, unknown>)['error'];
  if (typeof raw !== 'string') {
    return undefined;
  }
  // Strips control characters before bounding the length: the body is attacker-influenced and
  // this string may eventually reach a render surface, so nothing below U+0020 (bar the trim) or
  // DEL survives.
  // eslint-disable-next-line no-control-regex
  const cleaned = raw.replace(/[\x00-\x1f\x7f]/g, '').trim();
  return cleaned.length === 0 ? undefined : cleaned.slice(0, MAX_REFUSAL_REASON_CHARS);
}

interface RawFrame {
  event: string | null;
  data: string;
}

/** Splits `buffer` on SSE frame boundaries (a blank line). Keep-alive comment lines (`: ...`) are
 * dropped, and a frame with no `data:` line at all (a bare comment frame) yields nothing — mirrors
 * `waggle-server`'s own `SseTurnClient` test harness (CONTRACT.md addendum). Returns the frames
 * found plus whatever incomplete tail remains for the next chunk. */
export function parseSseChunk(buffer: string): { frames: RawFrame[]; rest: string } {
  const frames: RawFrame[] = [];
  let rest = buffer;
  let boundary: number;
  while ((boundary = rest.indexOf('\n\n')) !== -1) {
    const raw = rest.slice(0, boundary).replace(/\r/g, '');
    rest = rest.slice(boundary + 2);
    let event: string | null = null;
    const dataLines: string[] = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith(':') || line.length === 0) {
        continue;
      }
      if (line.startsWith('event:')) {
        event = line.slice('event:'.length).trim();
      } else if (line.startsWith('data:')) {
        dataLines.push(line.slice('data:'.length).trim());
      }
    }
    if (dataLines.length > 0) {
      frames.push({ event, data: dataLines.join('\n') });
    }
  }
  return { frames, rest };
}

/** The production `OpenTurnStream`: opens a real `fetch` request and reads its streaming body as
 * Server-Sent Events, decoding each frame into a {@link TurnEventDto} as it arrives (never waiting
 * for the whole response — a turn can run for the whole of `TurnBounds.timeout`, and the point of
 * the overlay is watching it live). */
export function createFetchTurnStream(fetchImpl: typeof fetch): OpenTurnStream {
  return (request, handlers) => {
    let closed = false;
    const controller = new AbortController();
    void (async () => {
      let response: Response;
      try {
        response = await fetchImpl(request.url, {
          method: request.method,
          headers: request.headers,
          body: request.body,
          signal: controller.signal,
        });
      } catch (error) {
        // The request never completed — admission is unknown to the client, not refused.
        if (!closed) {
          handlers.onError({ kind: 'transport', cause: error });
        }
        return;
      }
      if (!response.ok) {
        // The server answered — definitively, and definitively refused. Extract `reason` from the
        // body (sanitized, length-bounded; `undefined` on a non-JSON body) for the caller's reason
        // whitelist.
        const reason = await extractRefusalReason(response);
        if (!closed) {
          handlers.onError({ kind: 'refusal', status: response.status, reason });
        }
        return;
      }
      if (response.body === null) {
        // **`beekeeper-progress-indicator` task review F6.** A 2xx with no streamable body is NOT a
        // refusal — the server may have admitted the turn; this client simply never gets its events
        // over this connection. Admission is unknown, exactly like a dropped connection.
        if (!closed) {
          handlers.onError({ kind: 'transport', cause: new Error('response ok but body is null') });
        }
        return;
      }
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      try {
        for (;;) {
          const { value, done } = await reader.read();
          if (done) {
            break;
          }
          if (closed) {
            return;
          }
          buffer += decoder.decode(value, { stream: true });
          const { frames, rest } = parseSseChunk(buffer);
          buffer = rest;
          for (const frame of frames) {
            if (closed) {
              return;
            }
            const event = turnEventFromJson(safeJsonParse(frame.data));
            if (event !== null) {
              handlers.onEvent(event);
            }
          }
        }
      } catch (error) {
        // The body stopped being readable mid-stream — the connection failed, not the server.
        if (!closed) {
          handlers.onError({ kind: 'transport', cause: error });
        }
        return;
      }
      if (!closed) {
        handlers.onDone();
      }
    })();
    return {
      close: () => {
        closed = true;
        controller.abort();
      },
    };
  };
}

export interface ChatSseClientConfig {
  /** e.g. `http://127.0.0.1:8080` — no trailing slash required. */
  baseUrl: string;
  /** The sandbox's own scope id. */
  scope: string;
  /**
   * The route family this client speaks, as the first path segment: `rounds` (the default, and
   * every rounds-mode caller) or `live` (the pvp prototype's `/live/{colony}/chat/...`, whose
   * frames, cursors and DTOs are byte-identical — see `net/live-chat.ts`). A parameter rather
   * than a second client, because nothing below the path differs.
   */
  pathPrefix?: string;
  /** The bearer credential. */
  token: string;
  fetchImpl?: typeof fetch;
  /** Defaults to {@link createFetchTurnStream} over `fetchImpl`; tests inject a synthetic fake. */
  openStream?: OpenTurnStream;
}

/** The chat SSE client. One instance is scoped to one sandbox and one credential, exactly like
 * `RoundsHttpClient` — there is deliberately no way to point it at another player's scope. */
export class ChatSseClient {
  private readonly baseUrl: string;
  private readonly scope: string;
  private readonly pathPrefix: string;
  private readonly token: string;
  private readonly fetchImpl: typeof fetch;
  private readonly openStream: OpenTurnStream;

  constructor(config: ChatSseClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.scope = config.scope;
    this.pathPrefix = config.pathPrefix ?? 'rounds';
    this.token = config.token;
    this.fetchImpl = config.fetchImpl ?? globalThis.fetch.bind(globalThis);
    this.openStream = config.openStream ?? createFetchTurnStream(this.fetchImpl);
  }

  private path(suffix: string): string {
    return `${this.baseUrl}/${this.pathPrefix}/${this.scope}${suffix}`;
  }

  private authHeaders(extra?: Record<string, string>): Record<string, string> {
    return { Authorization: `Bearer ${this.token}`, ...extra };
  }

  /** `POST /chat/messages` — opens a turn's live event stream. `targetType` is a non-binding hint
   * (`waggle-beekeeper`'s `TurnInputs::selected_type`), `null` when the player addressed the
   * colony as a whole rather than one open bee-type sheet. The server resolves it against the
   * colony's roster and renders `null` — or a name the roster does not declare — as *no type
   * selected*, rather than substituting the birth type. */
  sendMessage(text: string, targetType: string | null, handlers: TurnStreamHandlers): SseSubscription {
    const body: Record<string, unknown> = { text, targetType };
    return this.openStream(
      {
        method: 'POST',
        url: this.path('/chat/messages'),
        headers: this.authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
      },
      handlers,
    );
  }

  /** `GET /chat/turns/{turnId}/events?afterCursor=` — reattaches to a turn already in flight
   * (a reload, or a dropped connection resuming from the last received cursor). */
  attachTurn(turnId: string, afterCursor: number, handlers: TurnStreamHandlers): SseSubscription {
    return this.openStream(
      {
        method: 'GET',
        url: this.path(`/chat/turns/${encodeURIComponent(turnId)}/events?afterCursor=${afterCursor}`),
        headers: this.authHeaders(),
      },
      handlers,
    );
  }

  /** `POST /chat/turns/{turnId}/stop`. `false` on any failure (a dead transport, an already-settled
   * turn refused) — the caller treats "did not confirm stopped" the same regardless of why. */
  async stopTurn(turnId: string): Promise<boolean> {
    let response: Response;
    try {
      response = await this.fetchImpl(this.path(`/chat/turns/${encodeURIComponent(turnId)}/stop`), {
        method: 'POST',
        headers: this.authHeaders(),
      });
    } catch {
      return false;
    }
    if (!response.ok) {
      return false;
    }
    const body = (await safeResponseJson(response)) as Record<string, unknown> | null;
    return body?.['stopped'] === true;
  }

  /** `GET /chat/thread`. `null` on any failure — a thread that could not be read is "no thread to
   * show", never fabricated as empty. */
  async fetchThread(): Promise<ThreadDto | null> {
    let response: Response;
    try {
      response = await this.fetchImpl(this.path('/chat/thread'), {
        method: 'GET',
        headers: this.authHeaders(),
      });
    } catch {
      return null;
    }
    if (!response.ok) {
      return null;
    }
    return threadDtoFromJson(await safeResponseJson(response));
  }

  /** `DELETE /chat/thread` — clears the conversation and starts a new one (WAG-94). `false` on any
   * failure, exactly as `stopTurn` reports one, and for the same reason: the caller treats "the
   * server did not confirm it cleared" the same however it failed, and a `false` here is what
   * stops a conversation the server still holds being replaced on screen by an empty one. */
  async clearThread(): Promise<boolean> {
    let response: Response;
    try {
      response = await this.fetchImpl(this.path('/chat/thread'), {
        method: 'DELETE',
        headers: this.authHeaders(),
      });
    } catch {
      return false;
    }
    if (!response.ok) {
      return false;
    }
    const body = (await safeResponseJson(response)) as Record<string, unknown> | null;
    return body?.['cleared'] === true;
  }
}
